import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agent-behavior-settings/utils';

type AutoSaveField = {
  value: unknown;
  delay: number;
  onSave: (values: AgentBehaviorSettingsForm) => Promise<void>;
};

type UseAutoSaveOptions = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  isInitialMount: React.MutableRefObject<boolean>;
  hasData: boolean;
  fields: Record<string, AutoSaveField>;
};

export const useAutoSave = ({ form, isInitialMount, hasData, fields }: UseAutoSaveOptions) => {
  const lastSavedValues = useRef<Record<string, unknown>>({});
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  const readyTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!hasData) return;
    const initialValues: Record<string, unknown> = {};
    Object.keys(fields).forEach((key) => {
      initialValues[key] = fields[key].value;
    });
    lastSavedValues.current = initialValues;

    readyTimer.current = setTimeout(() => {
      isInitialMount.current = false;
    }, 500);

    return () => {
      if (readyTimer.current) {
        clearTimeout(readyTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData]);

  useEffect(() => {
    if (!hasData || isInitialMount.current) {
      return;
    }

    Object.entries(fields).forEach(([key, field]) => {
      if (field.value === undefined) return;

      if (lastSavedValues.current[key] !== field.value) {
        if (timeoutRefs.current[key]) {
          clearTimeout(timeoutRefs.current[key]);
        }

        timeoutRefs.current[key] = setTimeout(async () => {
          const formValues = form.getValues();
          await field.onSave(formValues);
          lastSavedValues.current[key] = field.value;
          delete timeoutRefs.current[key];
        }, field.delay);
      }
    });

    const currentTimeouts = timeoutRefs.current;
    return () => {
      Object.values(currentTimeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, [hasData, isInitialMount, form, fields]);

  return lastSavedValues;
};
